import React, { type ReactNode, useState } from 'react'
import cx from 'classnames'

import { type UrlParameter } from '@lib/parameters'

import RadioGroup from '@components/radio-group'
import RadioItem from '@components/radio-item'
import Icon from '@components/icon'
import Drawer from '@components/drawer'

interface ListOption {
  type: string
  title: string
}

interface CollectionListboxProps {
  id: string
  name: string
  label: string
  drawerTitle: string
  options: ListOption[]
  activeOption: string | null
  before?: ReactNode
  after?: ReactNode
  onChange?: (newParameters: UrlParameter[]) => void
  className?: string
}

const CollectionListDrawer = ({
  id,
  name,
  label,
  drawerTitle,
  options,
  activeOption,
  before,
  after,
  onChange = () => null,
  className,
}: CollectionListboxProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const open = () => setIsOpen(true)
  const close = () => setIsOpen(false)

  const currentOption =
    options.find((option) => option.type === activeOption) ?? options[0]

  const handleChange = (value: string) => {
    onChange([{ name, value }])
    setIsOpen(false)
  }

  return (
    <div className={cx('relative z-20', className)}>
      <span id={`${id}-label`} className="sr-only">
        {label}
      </span>

      <button
        onClick={open}
        aria-expanded={isOpen}
        aria-labelledby={`${id}-label`}
        className="inline-flex gap-x-1.5 text-sm"
      >
        {before}
        {after}
      </button>

      <Drawer
        direction="right"
        isOpen={isOpen}
        onClose={close}
        className="max-w-md sm:max-w-sm"
      >
        <div className="border-b p-5 flex justify-between gap-5">
          <h3 className="is-h4">{drawerTitle}</h3>
          <button className="text-lg" onClick={close}>
            <Icon name="Close" id="close-filter-drawer-icon" />
          </button>
        </div>

        <RadioGroup
          value={currentOption.type}
          onChange={(value) => handleChange(value)}
          role="listbox"
          className="flex flex-col p-2"
        >
          {options.map((option, index) => (
            <RadioItem
              key={index}
              value={option.type}
              role="option"
              aria-selected={
                option.type === currentOption.type ? 'true' : 'false'
              }
              className={cx(
                'text-pageText px-2 py-2 text-sm text-left',
                'hover:opacity-50',
                { 'font-medium': option.type === currentOption.type },
              )}
            >
              {option.title}
            </RadioItem>
          ))}
        </RadioGroup>
      </Drawer>
    </div>
  )
}

export default CollectionListDrawer
